import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { dictionary } from "../../../utils/dictionary";
import { useAuth } from "../../../context/AuthContext";
import { type PreviousProduct, useOnboardingStore } from "../../../store/onboardingStore";

interface SearchPreviousProductsProps {
  previousProducts: PreviousProduct[];
  isPreviousProductsLoading: boolean;
  handlePreviousProductSelect: (product: PreviousProduct) => void;
  selectedProductId?: string | null;
  searchProducts?: (search: string) => Promise<PreviousProduct[]>;
}

function productMatchesSearch(product: PreviousProduct, search: string) {
  const normalizedSearch = search.toLowerCase();
  return [product.name, product.description, product.id, product.price]
    .map((value) => String(value ?? "").toLowerCase())
    .some((value) => value.includes(normalizedSearch));
}

function SearchPreviousProducts({
  previousProducts,
  isPreviousProductsLoading,
  handlePreviousProductSelect,
  selectedProductId,
  searchProducts,
}: SearchPreviousProductsProps) {
  const { language } = useAuth();
  const storeSearchPreviousProducts = useOnboardingStore((state) => state.searchPreviousProducts);
  const searchPreviousProducts = searchProducts ?? storeSearchPreviousProducts;
  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState<PreviousProduct[]>(() =>
    Array.isArray(previousProducts) ? previousProducts : []
  );
  const [selectedOption, setSelectedOption] = useState<PreviousProduct | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  
  const safeProducts = options;
  const fallbackProducts = previousProducts;
  const selectedProducts = useMemo(
    () => [selectedOption, ...safeProducts, ...fallbackProducts].filter(
      (product): product is PreviousProduct => Boolean(product)
    ),
    [fallbackProducts, safeProducts, selectedOption]
  );
  const selectedProduct = selectedProductId
    ? selectedProducts.find((product) => product?.id === selectedProductId) ?? null
    : null;

  useEffect(() => {
    if (!selectedProductId) {
      setSelectedOption(null);
    }
  }, [selectedProductId]);

  useEffect(() => {
    const normalizedSearch = searchValue.trim();

    if (!normalizedSearch) {
      setIsSearching(false);
      setOptions(fallbackProducts);
      return;
    }

    let shouldIgnore = false;
    const localMatches = fallbackProducts.filter((product) => productMatchesSearch(product, normalizedSearch));
    setIsSearching(true);
    setOptions(localMatches);

    const timeoutId = window.setTimeout(() => {
      searchPreviousProducts(normalizedSearch)
        .then((products) => {
          if (!shouldIgnore) {
            setOptions(products);
          }
        })
        .catch(() => {
          if (!shouldIgnore) {
            setOptions(localMatches);
          }
        })
        .finally(() => {
          if (!shouldIgnore) {
            setIsSearching(false);
          }
        });
    }, 450);

    return () => {
      shouldIgnore = true;
      window.clearTimeout(timeoutId);
    };
  }, [fallbackProducts, searchPreviousProducts, searchValue]);

  const loading = isSearching || (safeProducts.length === 0 && isPreviousProductsLoading);
  
  const getNoOptionsText = () => {
    if (loading) {
      return dictionary["loading-products"]?.[language];
    }
    
    if (safeProducts.length === 0) {
      return dictionary["no-products-found"]?.[language];
    }
    
    return dictionary["no-products-found-onboarding"]?.[language];
  };
  
  const getPlaceholder = () => {
    if (loading) {
      return dictionary["loading-products"]?.[language];
    }
    
    return dictionary["search-previous-products-placeholder"]?.[language];
  };
  
  return (
    <div className="flex-1 relative">
      <Autocomplete
        id="previous-products-autocomplete"
        options={safeProducts}
        loading={loading}
        getOptionLabel={(option) => option?.name || ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={selectedProduct}
        filterOptions={(options) => options}
        onOpen={() => {
          if (!searchValue.trim()) {
            setOptions(fallbackProducts);
          }
        }}
        onInputChange={(_, value, reason) => {
          if (reason === "input") {
            setSearchValue(value);
          }
          if (reason === "clear") {
            setSearchValue("");
            setOptions(fallbackProducts);
          }
        }}
        onChange={(_, product) => {
          if (!product) return;
          setSelectedOption(product);
          setSearchValue("");
          setOptions(fallbackProducts);
          handlePreviousProductSelect(product);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={getPlaceholder()}
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <Search className="w-5 h-5 text-gray-500 mr-2" />
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              className: "border border-gray-100 rounded-md bg-white",
              style: {
                color: 'black',
                WebkitTextFillColor: 'black', // For webkit browsers
              },
            }}
          />
        )}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement> & { 'data-option-index'?: number }, option) => (
          <li {...props} key={option.id || `product-${props['data-option-index']}`} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
            <div className="flex items-center">
              {option.images && option.images[0] && (
                <img
                  src={option.images[0]}
                  alt={option.name}
                  className="w-10 h-10 object-cover rounded-md mr-3"
                />
              )}
              <div>
                <div className="font-medium">{option.name || 'Unnamed Product'}</div>
                <div className="text-sm text-gray-500">${option.price || '0.00'}</div>
              </div>
            </div>
          </li>
        )}
        noOptionsText={getNoOptionsText()}
        loadingText={dictionary["loading-products"]?.[language]}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: '48px',
            borderRadius: '16px',
            padding: '0 14px',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--fluxi-primary-hex, #4368E5)',
              borderWidth: '1px',
            },
          },
          '& .MuiAutocomplete-endAdornment': {
            right: '8px',
          },
          '& .MuiAutocomplete-inputRoot': {
            paddingTop: '0 !important',
            paddingBottom: '0 !important',
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiAutocomplete-input': {
            height: '40px',
            padding: '0 !important',
            color: 'black',
          }
        }}
      />
    </div>
  );
}

export default SearchPreviousProducts;
